import React, { useState } from 'react';
import { Filter, Search, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, XCircle, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const CATEGORY_LABELS = {
  ride_payment: 'Ride',
  delivery_payment: 'Delivery',
  commission: 'Commission',
  withdrawal: 'Withdrawal',
  refund: 'Refund',
  bonus: 'Bonus',
  adjustment: 'Adjustment',
  escrow_hold: 'Escrow Hold',
  escrow_release: 'Escrow Release',
  driver_earnings_transfer: 'Earnings Transfer',
};

const CATEGORY_COLORS = {
  ride_payment: 'bg-blue-500/20 text-blue-300',
  delivery_payment: 'bg-purple-500/20 text-purple-300',
  commission: 'bg-orange-500/20 text-orange-300',
  withdrawal: 'bg-rose-500/20 text-rose-300',
  refund: 'bg-yellow-500/20 text-yellow-300',
  bonus: 'bg-emerald-500/20 text-emerald-300',
  adjustment: 'bg-gray-500/20 text-gray-300',
  escrow_hold: 'bg-indigo-500/20 text-indigo-300',
  escrow_release: 'bg-teal-500/20 text-teal-300',
  driver_earnings_transfer: 'bg-cyan-500/20 text-cyan-300',
};

const TransactionHistory = ({ transactions, loading, currency, onSelectTransaction }) => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const categories = ['all', 'ride_payment', 'delivery_payment', 'commission', 'withdrawal', 'refund', 'bonus', 'adjustment'];

  const filtered = (transactions || [])?.filter(tx => {
    const matchSearch = !search || tx?.description?.toLowerCase()?.includes(search?.toLowerCase()) || tx?.id?.includes(search);
    const matchCategory = categoryFilter === 'all' || tx?.category === categoryFilter;
    const matchType = typeFilter === 'all' || tx?.ledger_type === typeFilter;
    return matchSearch && matchCategory && matchType;
  });

  const formatAmount = (amount) => new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2 })?.format(amount || 0);
  const formatDate = (dateStr) => {
    try { return format(new Date(dateStr), 'MMM d, yyyy · h:mm a'); }
    catch { return dateStr; }
  };

  const StatusIcon = ({ status }) => {
    if (status === 'completed') return <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />;
    if (status === 'failed') return <XCircle className="w-3.5 h-3.5 text-rose-400" />;
    return <Clock className="w-3.5 h-3.5 text-yellow-400" />;
  };

  return (
    <div className="bg-[#0d1f3c] rounded-2xl border border-white/10 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-white/10">
        <h3 className="text-white font-semibold text-lg mb-4">Transaction History</h3>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Search transactions..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#ffd700]/50"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2 py-1">
            <Filter className="w-3 h-3 text-white/40" />
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e?.target?.value)}
              className="bg-transparent text-white/70 text-xs focus:outline-none cursor-pointer"
            >
              <option value="all">All Types</option>
              <option value="credit">Credits</option>
              <option value="debit">Debits</option>
            </select>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {categories?.map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                  categoryFilter === cat
                    ? 'bg-[#ffd700] text-[#0a1628]'
                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                }`}
              >
                {cat === 'all' ? 'All' : CATEGORY_LABELS?.[cat] || cat}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Transaction List */}
      <div className="divide-y divide-white/5">
        {loading ? (
          Array.from({ length: 5 })?.map((_, i) => (
            <div key={i} className="p-4 flex items-center gap-4 animate-pulse">
              <div className="w-10 h-10 rounded-xl bg-white/10" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-48 bg-white/10 rounded" />
                <div className="h-3 w-32 bg-white/5 rounded" />
              </div>
              <div className="h-5 w-20 bg-white/10 rounded" />
            </div>
          ))
        ) : filtered?.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-white/30 text-sm">No transactions found</p>
          </div>
        ) : (
          filtered?.map(tx => (
            <button
              key={tx?.id}
              onClick={() => onSelectTransaction?.(tx)}
              className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-colors text-left group"
            >
              {/* Icon */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                tx?.ledger_type === 'credit' ? 'bg-emerald-500/20' : 'bg-rose-500/20'
              }`}>
                {tx?.ledger_type === 'credit'
                  ? <ArrowDownLeft className="w-5 h-5 text-emerald-400" />
                  : <ArrowUpRight className="w-5 h-5 text-rose-400" />
                }
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-white text-sm font-medium truncate">{tx?.description || 'Transaction'}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${CATEGORY_COLORS?.[tx?.category] || 'bg-gray-500/20 text-gray-300'}`}>
                    {CATEGORY_LABELS?.[tx?.category] || tx?.category}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusIcon status={tx?.status} />
                  <span className="text-white/40 text-xs capitalize">{tx?.status}</span>
                  <span className="text-white/20 text-xs">·</span>
                  <span className="text-white/30 text-xs">{formatDate(tx?.created_at)}</span>
                </div>
              </div>

              {/* Amount */}
              <div className="text-right flex-shrink-0">
                <p className={`font-semibold text-sm ${tx?.ledger_type === 'credit' ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {tx?.ledger_type === 'credit' ? '+' : '-'}{currency || 'KES'} {formatAmount(tx?.amount)}
                </p>
                <p className="text-white/20 text-xs mt-0.5">{tx?.id?.slice(0, 8)}...</p>
              </div>

              <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors flex-shrink-0" />
            </button>
          ))
        )}
      </div>
      {filtered?.length > 0 && (
        <div className="p-4 border-t border-white/5 text-center">
          <p className="text-white/30 text-xs">{filtered?.length} transaction{filtered?.length !== 1 ? 's' : ''} shown</p>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
